/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { Injectable } from '@angular/core';
import { AngularFireDatabase } from '@angular/fire/compat/database';
import { Subject, BehaviorSubject, Observable, of, combineLatest, Subscription } from 'rxjs';
import {
	ActualPhase,
	Ticket,
	TicketFields,
	ProjectMember,
	Role,
	Location,
	Priority,
	Variance,
	TicketType,
	PlanAccessLevel,
} from '@touchplan/common';
import { FancyActionTrackerFactory } from 'src/app/common/fancy-firebase/services/fancy-action-tracker-factory.service';
import { debounceTime, filter, map, startWith, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { fancyFirebaseGetters, fancyFirebaseReferencer } from '@touchplan/fancy-referencer';
import { CustomFieldsApi } from 'src/app/app-data/state/CustomFieldsApi';
import { FancyFirebaseColorsRolesLocationsApiBuilder } from 'src/app/services/fancy-firebase-api-builder/fancy-firebase-colors-roles-locations-api-builder.service';
import { ColorType } from 'src/app/app-data/constants/color-type.enum';
import {
	sortLocationsByName,
	sortRolesByName,
	sortTicketsByCreationDate,
	sortTicketsByEndDate,
} from 'src/app/utils/array-util';
import {
	sortArray,
	subjectSubscribeWithoutComplete,
} from 'src/app/plain-javascript/services/rxjs-internal-extensions';
import { FancyFirebaseProjectMembersApiBuilder } from 'src/app/services/fancy-firebase-api-builder/fancy-firebase-project-members-api-builder.service';
import { FancyFirebaseProjectsApiBuilder } from 'src/app/services/fancy-firebase-api-builder/fancy-firebase-projects-api-builder.service';
import { CoreState } from 'src/app/app-data/interfaces/app-state.interface';
import { PlansApi } from 'src/app/app-data/state/PlansApi';
import { ProjectMembersApi, userProjectMemberObserver } from 'src/app/app-data/state/ProjectMembersApi';
import { RolesApi } from 'src/app/app-data/state/RolesApi';
import { LocationsApi } from 'src/app/app-data/state/LocationsApi';
import { ColorsApi } from 'src/app/app-data/state/ColorsApi';
import { FancyFirebaseCustomFieldsApiBuilder } from 'src/app/services/fancy-firebase-api-builder/fancy-firebase-customfields-api-builder.service';
import { AppState } from 'src/app/services/app-state.service';
import { ProjectObject } from 'src/app/common/fancy-firebase/objects/ProjectObject';
import { PlanAccessCheck } from 'src/app/services/PlanAccessCheck';
import { VirtualListManager } from './virtual-list-manager';
import { QueryFactory } from './query-factory';
import { TicketIndexLogger } from './ticket-index-logger.service';
import { FeatureFlagManager } from 'src/app/services/FeatureFlagManager';
import { Plan } from '@touchplan/common';
import { TicketList } from 'src/app/common/fancy-firebase-ticket/services/ticket-factory';
import { PlanObject } from 'src/app/common/fancy-firebase/objects/PlanObject';
import { FancySimpleTicketGrouper } from 'src/app/app-data/models/FancyGrouper';
import { makeTicketEditObservable } from 'src/app/common/fancy-firebase-ticket/utils/make-ticket-edit-observer';
import { PriorityList } from 'src/app/common/fancy-firebase/lists/PriorityList';
import { GroupedTicket } from '@touchplan/ticket';
import { TicketListMaker } from './ticket-list-maker.service';
import { FancyFirebaseList } from 'src/app/common/fancy-firebase/base/FancyFirebaseList';
import { FancyAction } from 'src/app/common/fancy-firebase/base/FancyAction';
import { CalendarWorkdays } from '@touchplan/ticket';
import {
	changeTicketViewDataToDatabase,
	changeTicketViewToDefault,
	changeTicketViewToEdit,
	persistTicketData,
	updateTicketLiveData,
} from './ticket-writer';
import { VarianceList } from 'src/app/common/fancy-firebase/lists/VarianceList';
import { LoggerServiceFactory } from 'src/app/common/logging/LoggerServiceFactory';
import { ILoggerService } from '@touchplan/logging';
import { FancyFirebasePlansApiBuilder } from 'src/app/services/fancy-firebase-api-builder/fancy-firebase-plans-api-builder.service';

import firebase from 'firebase/compat/app';

export interface IFilterByStatusType {
	toDo: boolean;
	completed: boolean;
}

export type IFilterBySelectedRoleIdsType = string[];

export interface IFilterBySelectedLocationsType {
	noLocation: boolean;
	locationIds: string[];
}

interface IDestroyable {
	destroy();
}

@Injectable({
	providedIn: 'root',
})
export class TicketService implements CoreState {
	/** A simple boolean to check if data has been loaded or not. This should be eventually replaced by CoreState's lifecycle hooks */
	public loaded = false;

	public filterBySelectedRoleIds$ = new BehaviorSubject<IFilterBySelectedRoleIdsType>(null);
	public filterBySelectedLocationIds$ = new BehaviorSubject<IFilterBySelectedLocationsType>(null);
	public filterByStatus$ = new BehaviorSubject<IFilterByStatusType>({ toDo: true, completed: true });

	// This has a 50ms debounce time on it; see below and TP-3285
	public ticketList$: BehaviorSubject<Ticket[]> = new BehaviorSubject(null); // Not used anymore?
	public filteredTicketList$: BehaviorSubject<Ticket[]> = new BehaviorSubject(null);

	public ticket: Ticket;
	public authedProjectMember$: BehaviorSubject<ProjectMember> = new BehaviorSubject(null);
	private ticketGrouper: FancySimpleTicketGrouper;
	private shutdownSubject$: Subject<void> = new Subject();

	/** contains the current groupedTicket set */
	public selectedTicket$: BehaviorSubject<GroupedTicket> = new BehaviorSubject(null);
	/** Tied to the lifecycle of the grouped/ selected ticket, true when it's defined, false when it's not */
	public onTicketDetailsView$ = this.selectedTicket$.pipe(map((t) => t && t.srcList.size > 0));

	// public sortedTicketList$: Observable<Ticket[]>;
	public sortBy$ = new BehaviorSubject('start_date');

	//various sorted lists in observable form derived from and connected to the FancyFirebaseLists
	public roles$: BehaviorSubject<Role[]> = new BehaviorSubject([]);
	public locations$: BehaviorSubject<Location[]> = new BehaviorSubject([]);
	public projectMembers$: BehaviorSubject<ProjectMember[]> = new BehaviorSubject([]);
	public priorityList$: BehaviorSubject<Priority[]> = new BehaviorSubject([]);
	public varianceList$: BehaviorSubject<Variance[]> = new BehaviorSubject([]);

	public saveCustomFieldsRequested$ = new Subject<void>();
	public cancelCustomFieldsRequested$ = new Subject<void>();

	public customFieldsApi: CustomFieldsApi;
	public plansApi: PlansApi;
	public projectMembersApi: ProjectMembersApi;
	public rolesApi: RolesApi;
	public locationsApi: LocationsApi;
	public roleColorApi: ColorsApi;
	public locationColorApi: ColorsApi;
	public ticketList: TicketList;
	public calendarWorkdays: CalendarWorkdays;
	private priorityList: PriorityList;
	private varianceList: VarianceList;
	public projectId = new BehaviorSubject<string>(null);
	public plan$ = new BehaviorSubject<Plan>(null);

	public secondaryTicketList: TicketList;
	private specialSecondaryUnsubList: Subscription[] = [];

	private angularLand = {
		angularFireDatabase: this.angularFireDatabase,
		fancyActionTrackerFactory: this.fancyActionTrackerFactory,
	};

	public virtualListManager: VirtualListManager;

	private destroyables: IDestroyable[] = [];
	private logger: ILoggerService;

	constructor(
		private appState: AppState,
		public planAccessCheck: PlanAccessCheck,
		private angularFireDatabase: AngularFireDatabase,
		private fancyActionTrackerFactory: FancyActionTrackerFactory,
		private fancyFirebaseColorsRolesLocationApiBuilder: FancyFirebaseColorsRolesLocationsApiBuilder,
		private fancyFirebaseProjectMembersApiBuilder: FancyFirebaseProjectMembersApiBuilder,
		private fancyFirebaseProjectsApiBuilder: FancyFirebaseProjectsApiBuilder,
		private fancyFirebaseCustomFieldsApiBuilder: FancyFirebaseCustomFieldsApiBuilder,
		private fancyFirebasePlansApiBuilder: FancyFirebasePlansApiBuilder,
		private queryFactory: QueryFactory,
		private featureFlagManager: FeatureFlagManager,
		private ticketIndexLogger: TicketIndexLogger,
		private ticketListMaker: TicketListMaker
	) {
		this.logger = LoggerServiceFactory.create('TicketService');
	}

	//currently unused, likely legacy
	// public fetchTicket(projectId: string, planId: string, ticketId: string) {
	// 	const ticketPath = fancyFirebaseGetters.getTicketPath(projectId, planId, ticketId);
	// 	return new TicketObject(this.angularLand, fancyFirebaseReferencer.getLegacyRef<firebase.database.Reference>(ticketPath, projectId));
	// }

	//drives the observable based dynamic sorting
	private getCompareFunction(val: string): (a: Ticket, b: Ticket) => number {
		let compareFunc: (a: Ticket, b: Ticket) => number;
		switch (val) {
			case 'end_date':
				compareFunc = sortTicketsByEndDate;
				break;
			case 'start_date':
			default:
				compareFunc = sortTicketsByCreationDate;
				break;
		}
		return compareFunc;
	}

	public sortTickets(sortBy: string) {
		this.sortBy$.next(sortBy);
	}

	//initializes all the apis (and lists) and sets up the sorted versions of the list observables
	public setupData(projectId: string, planId: string) {
		this.projectId.next(projectId);
		const roleColorApi = this.fancyFirebaseColorsRolesLocationApiBuilder.getColorsApi(
			projectId,
			ColorType.Roles
		);
		const locationColorApi = this.fancyFirebaseColorsRolesLocationApiBuilder.getColorsApi(
			projectId,
			ColorType.Locations
		);
		const rolesApi = this.fancyFirebaseColorsRolesLocationApiBuilder.getRolesApi(projectId, roleColorApi);
		const locationsApi = this.fancyFirebaseColorsRolesLocationApiBuilder.getLocationsApi(
			projectId,
			locationColorApi
		);

		rolesApi.data.list$
			.pipe(
				map((roleMap) => Array.from(roleMap.values())),
				sortArray(of(sortRolesByName)),
				takeUntil(this.shutdownSubject$)
			)
			.subscribe(subjectSubscribeWithoutComplete(this.roles$));
		locationsApi.data.list$
			.pipe(
				map((locationMap) => Array.from(locationMap.values())),
				sortArray(of(sortLocationsByName)),
				takeUntil(this.shutdownSubject$)
			)
			.subscribe(subjectSubscribeWithoutComplete(this.locations$));
		const projectMembersApi = this.fancyFirebaseProjectMembersApiBuilder.getProjectMembersApi(
			projectId,
			rolesApi
		);
		projectMembersApi.data.list$
			.pipe(
				map((projectMembersMap) => Array.from(projectMembersMap.values())),
				takeUntil(this.shutdownSubject$)
			)
			.subscribe(subjectSubscribeWithoutComplete(this.projectMembers$));

		const priorityList = this.fancyFirebaseProjectsApiBuilder.getPriorityList(projectId);
		priorityList.list$
			.pipe(
				map((priorityMap) => Array.from(priorityMap.values()).sort((a, b) => a.sortOrder - b.sortOrder)),
				takeUntil(this.shutdownSubject$)
			)
			.subscribe(subjectSubscribeWithoutComplete(this.priorityList$));

		const varianceList = this.fancyFirebaseProjectsApiBuilder.getVarianceList(projectId);
		//kind of pointless to sort/ array this here instead of at display time, buuut... consistency > performance
		varianceList.list$
			.pipe(
				map((varianceMap) => Array.from(varianceMap.values())),
				takeUntil(this.shutdownSubject$)
			)
			.subscribe(subjectSubscribeWithoutComplete(this.varianceList$));
		const ticketList = this.constructTicketList(
			projectId,
			planId,
			rolesApi,
			locationsApi,
			projectMembersApi,
			priorityList,
			varianceList
		);
		ticketList.list$
			.pipe(
				debounceTime(50), // Fix for TP-3285
				map((ticketMap) => {
					// filtering out broken accepted constraints with missing roles.
					return Array.from(ticketMap.values()).filter((ticket) => {
						return !(
							ticket.view?.type === TicketType.Constraint &&
							!ticket.view.liveRole &&
							!ticket.view.roleId
						);
					});
				})
			)
			.subscribe(subjectSubscribeWithoutComplete(this.ticketList$));

		this.ticketGrouper = this.createTicketEditListener(ticketList);
		this.ticketGrouper.grouped$
			.pipe(takeUntil(this.shutdownSubject$))
			.subscribe(subjectSubscribeWithoutComplete(this.selectedTicket$));
		this.setupFiltering();

		this.startPlanAccessCheck(projectId, planId, projectMembersApi);

		const projectPath = fancyFirebaseGetters.getProjectsPath(projectId);
		const projectWorkdays = this.angularFireDatabase.object(
			fancyFirebaseReferencer
				.getLegacyRef<firebase.database.Reference>(projectPath, projectId)
				.child('/workShiftsByDow')
		);
		const projectExceptionsDaysPath = fancyFirebaseGetters.getProjectExceptionDaysPath(projectId);
		const projectExceptionDays = new FancyFirebaseList(
			this.angularLand,
			fancyFirebaseReferencer.getLegacyRef<firebase.database.Reference>(projectExceptionsDaysPath, projectId),
			FancyAction
		);

		const calendarWorkdays = new CalendarWorkdays(
			rolesApi.data.list$.asObservable(),
			projectWorkdays.valueChanges(),
			projectExceptionDays.list$.asObservable() as Observable<Map<string, boolean>>
		);
		this.destroyables.push(projectExceptionDays);

		const planPath = fancyFirebaseGetters.getPlanPath(projectId, planId);
		const plan = new PlanObject(
			this.angularLand,
			fancyFirebaseReferencer.getLegacyRef<firebase.database.Reference>(planPath, projectId)
		);
		plan.object$
			.pipe(takeUntil(this.shutdownSubject$))
			.subscribe(subjectSubscribeWithoutComplete(this.plan$));

		this.destroyables.push(plan);

		this.setup(
			rolesApi,
			roleColorApi,
			locationsApi,
			locationColorApi,
			projectMembersApi,
			priorityList,
			varianceList,
			ticketList,
			calendarWorkdays
		);
	}

	private constructTicketList(
		projectId: string,
		planId: string,
		rolesApi: RolesApi,
		locationsApi: LocationsApi,
		projectMembersApi: ProjectMembersApi,
		priorityList: PriorityList,
		varianceList: VarianceList
	) {
		const tempPseudoVirtualSnapshotChange$ = this.getTempPseudoVirtualSnapshotChange$(projectId, planId);
		const ticketList = this.ticketListMaker.constructTicketList(
			projectId,
			planId,
			rolesApi,
			locationsApi,
			projectMembersApi,
			priorityList,
			varianceList,
			tempPseudoVirtualSnapshotChange$
		);
		return ticketList;
	}

	// creates and kicks off a grouper mostly
	private createTicketEditListener(ticketList: TicketList) {
		const grouper = new FancySimpleTicketGrouper();
		grouper.start(makeTicketEditObservable(ticketList.rawEvent$));
		return grouper;
	}

	public setupFiltering() {
		const compareFunction$ = this.sortBy$.pipe(
			map((val) => this.getCompareFunction(val)),
			startWith(sortTicketsByCreationDate)
		);
		combineLatest([
			this.ticketList$,
			this.filterByStatus$,
			this.filterBySelectedRoleIds$,
			this.filterBySelectedLocationIds$,
		])
			.pipe(
				map((group) => {
					const unfilteredSortedList = group[0];
					const filterByToDo = group[1].toDo;
					const filterByCompleted = group[1].completed;
					const selectedRoleIdsToFilterBy = group[2];
					const selectedLocationIdsToFilterBy = group[3];
					if (!unfilteredSortedList || (unfilteredSortedList && !unfilteredSortedList.length)) {
						return null;
					}
					return unfilteredSortedList.filter((ticket) =>
						this.matchesFilter(
							ticket,
							filterByToDo,
							filterByCompleted,
							selectedRoleIdsToFilterBy,
							selectedLocationIdsToFilterBy
						)
					);
				}),
				sortArray(compareFunction$),
				takeUntil(this.shutdownSubject$)
			)
			.subscribe(subjectSubscribeWithoutComplete(this.filteredTicketList$));
	}

	private matchesFilter(
		ticket: Ticket,
		filterByToDo: boolean,
		filterByCompleted: boolean,
		selectedRoleIdsToFilterBy: IFilterBySelectedRoleIdsType,
		selectedLocationIdsToFilterBy: IFilterBySelectedLocationsType
	): boolean {
		const tf: TicketFields = ticket.view;
		if (!filterByToDo) {
			if (tf.actualPhase === ActualPhase.planned || tf.actualPhase === ActualPhase.plannedPreview) {
				return false;
			}
		}
		if (!filterByCompleted) {
			if (tf.actualPhase === ActualPhase.actualized || tf.actualPhase === ActualPhase.actualizing) {
				return false;
			}
		}

		let roleId = tf.roleId;
		if (!roleId) {
			if (tf.liveRole?.$id) {
				roleId = tf.liveRole?.$id;
			} else {
				this.logger.warn(
					"TP-3344: Ticket is missing both valid 'ticket.view.roleId' and 'ticket.view.liveRole.$id' fields",
					ticket
				); // Whut the...
			}
		}
		if (selectedRoleIdsToFilterBy && roleId) {
			if (!selectedRoleIdsToFilterBy.includes(roleId)) {
				return false;
			}
		}

		const locationId = tf.locationId;
		if (selectedLocationIdsToFilterBy) {
			if (locationId) {
				// The ticket has a location:
				if (!selectedLocationIdsToFilterBy.locationIds.includes(locationId)) {
					return false;
				}
			} else {
				// The ticket DOES NOT have a location:
				if (!selectedLocationIdsToFilterBy.noLocation) {
					return false;
				}
			}
		}

		return true;
	}

	private getTempPseudoVirtualSnapshotChange$(projectId: string, planId: string) {
		this.virtualListManager = new VirtualListManager(
			this.queryFactory,
			this.featureFlagManager,
			this.ticketIndexLogger
		);
		this.virtualListManager.startTicketIndexQuery(projectId, planId);
		return this.virtualListManager.ticketIndexQuery.ticketSnapshotChange$;

		/*
			MAYBE LATER

		this.virtualSortedTickets$ = combineLatest([
			this.ticketList$,
			this.virtualListManager.totalNumberOfTicket$,
			this.virtualListManager.firstTicket$,
			this.virtualListManager.lastTicket$
		]).pipe(
			debounceTime(500),
			map(quad => {
				const sortedListOfLoadedTickets = quad[0];
				const firstTicket = quad[1].payload.val();
				const lastTicket = quad[2].payload.val();
				const totalNumberOfTickets = quad[3];
				this.logger.log(`tickets = [... ${sortedListOfLoadedTickets.length} ...], totalNumberOfTickets = ${'unknown'}`);
				return this.createVirtualSortedTicketsArray(totalNumberOfTickets, firstTicket, lastTicket, sortedListOfLoadedTickets);
			})
		);
		*/
	}

	public handleRenderedRangeChange(start: number, end: number): void {
		// this.logger.log(`new range = [ ${start} .. ${end} ]`);
	}

	/*
		MAYBE LATER
	private createVirtualSortedTicketsArray(
		//totalNumberOfTickets: number,
		firstTicket: TicketFields,
		lastTicket: TicketFields,
		sortedListOfLoadedTickets: Ticket[],
	): Ticket[] {
		// Temporary until TP-2139 is really completed:
		return sortedListOfLoadedTickets;

		// TODO:  Fix me a la TP-2139:
		const result = Array(totalNumberOfTickets).fill(sortedListOfLoadedTickets[0]);
		result.splice(2, sortedListOfLoadedTickets.length, ...sortedListOfLoadedTickets); // This splice is just messing around stuff
		return result;
	}
	*/

	public startPlanAccessCheck(projectId: string, planId: string, projectMembersApi: ProjectMembersApi) {
		const projectPath = fancyFirebaseGetters.getProjectsPath(projectId);
		const projectObject = new ProjectObject(
			this.angularLand,
			fancyFirebaseReferencer.getLegacyRef<firebase.database.Reference>(projectPath, projectId)
		);
		userProjectMemberObserver(this.appState.user, of(projectMembersApi))
			.pipe(takeUntil(this.shutdownSubject$))
			.subscribe(subjectSubscribeWithoutComplete(this.authedProjectMember$));
		const plansApi = this.fancyFirebasePlansApiBuilder.getPlanApi(projectId);
		const planAccess$ = plansApi.planAccessApi.data.list$.pipe(map((list) => list.get(planId)));
		this.planAccessCheck.start(
			planAccess$,
			projectObject.object$.asObservable(),
			this.authedProjectMember$.asObservable()
		);
		this.destroyables.push(projectObject);
		this.destroyables.push(plansApi);
	}

	/**
	 * Checks both ticket lists for the ticketId, throws an error if it can't be found. Returns the selected ticket list.
	 */
	private getSelectedTicketList(ticketId: string) {
		//prioritize the secondaryTicketList if it exists, as it should only exist when it actually needs to
		if (this.secondaryTicketList && this.secondaryTicketList.syncList.has(ticketId)) {
			return this.secondaryTicketList;
		} else if (this.ticketList && this.ticketList.syncList.has(ticketId)) {
			return this.ticketList;
		}
		throw new Error('ticket does not exist');
	}

	/**
	 * Select a ticket to be edited, the whole edit sequence is driven like...
	 * - the details route resovler is hit. Either by hitting a list item or loading the page
	 * - the resolver calls this method (or the other one if not loaded)
	 * - this method sets that ticket to the edit renderType
	 * - the grouper is watching for tickets switched to edit and emits when that happens
	 * - the resolver is set to end when the the grouped ticket observable emits
	 * - when the resolver resolves, ticket details is loaded and can assume the ticket has already loaded
	 */
	public selectTicket(ticketId: string) {
		this.logger.log('select ticket', ticketId);
		const selectedTicketList = this.getSelectedTicketList(ticketId);
		const ticket = selectedTicketList.syncList.get(ticketId);

		changeTicketViewToEdit(ticketId, selectedTicketList);
	}

	//temporary v1 thing, take a ticket list, change all edit stuff back to default
	private unselectAllEditTickets(ticketList: TicketList) {
		const editTickets = ticketList.editTicket$.getValue();
		if (!editTickets) {
			return;
		}
		editTickets.forEach((ticket) => {
			changeTicketViewToDefault(ticket.$id, ticketList);
		});
	}

	/**
	 * Reverse of selection, kind of. This just takes everything currently being edited and unedits it.
	 * This one is trigged by the destruction of TicketDetails currently
	 */
	public unselectTicket() {
		this.unselectAllEditTickets(this.ticketList);

		if (this.secondaryTicketList) {
			this.unselectAllEditTickets(this.secondaryTicketList);

			this.secondaryTicketList.destroy();
			this.secondaryTicketList = null;
		}
		this.specialSecondaryUnsubList?.forEach((sub, i, arr) => {
			sub.unsubscribe();
			arr.shift();
		});
	}

	public applyLiveUpdate(ticketId: string, data: TicketFields) {
		const selectedTicketList = this.getSelectedTicketList(ticketId);
		updateTicketLiveData(ticketId, data, selectedTicketList);
	}

	public persistTicketData(ticketId: string) {
		this.planAccessCheck.hasRestrictedAccess$
			.pipe(take(1), takeUntil(this.shutdownSubject$))
			.subscribe((readOnlyBool) => {
				const readOnlyConfig = readOnlyBool ? { readOnlyWrite: true } : { readOnlyWrite: false };
				const selectedTicketList = this.getSelectedTicketList(ticketId);
				persistTicketData(ticketId, selectedTicketList, readOnlyConfig);
			});
	}

	public resetTicketEditDataToDatabase(ticketId: string) {
		const selectedTicketList = this.getSelectedTicketList(ticketId);
		changeTicketViewDataToDatabase(ticketId, selectedTicketList);
	}

	/**
	 * Special request that loads a single ticket into another ticket list and waits until it loads.
	 * Then registers that ticketList to this.secondaryList and sets that ticket as selected
	 */
	public fetchTicketFromDetailView(projectId: string, planId: string, ticketId: string) {
		this.logger.log('ticket.service fetchTicketFromDetailView', ticketId, this.selectedTicket$.getValue());
		this.setupData(projectId, planId);

		const secondaryList$ = this.ticketListMaker.waitForAndCreateSingleTicketList(
			projectId,
			planId,
			ticketId,
			this.rolesApi,
			this.locationsApi,
			this.projectMembersApi,
			this.priorityList,
			this.varianceList
		);
		return secondaryList$.pipe(
			//TODO: optimization, check again to see if the primary ticket list has the ticket _now_ and if it does, destroy this and use that one
			tap((secondaryList) => {
				this.secondaryTicketList = secondaryList;
			}),
			switchMap((secondaryList) => {
				if (!secondaryList) {
					return of(null as GroupedTicket);
				}
				const grouper = this.createTicketEditListener(secondaryList);
				//in theory this could be a memory leak in the future when the secondary list isn't destroyed...
				const sub = grouper.grouped$.subscribe(subjectSubscribeWithoutComplete(this.selectedTicket$));
				this.specialSecondaryUnsubList.push(sub);
				this.selectTicket(ticketId);

				return this.selectedTicket$.pipe(filter((t) => !!t));
			})
		);
	}

	public setupCustomFieldsApi(projectId: string, planId: string, projectMemberId: string) {
		const customFieldsApi = this.fancyFirebaseCustomFieldsApiBuilder.getCustomFieldsApi(
			projectId,
			planId,
			projectMemberId
		);
		this.customFieldsApi = customFieldsApi;
		return customFieldsApi;
	}

	public allowPlanAccessOnLoad$(): Observable<boolean> {
		return combineLatest({
			planAccessLevel: this.planAccessCheck.accessLevel$,
			plan: this.plan$.pipe(filter((plan) => !!plan)),
		}).pipe(
			map(
				({ planAccessLevel, plan }) =>
					!(planAccessLevel === PlanAccessLevel.archived || !!plan.view.deletedAt)
			)
		);
	}

	public setup(
		rolesApi: RolesApi,
		roleColorApi: ColorsApi,
		locationsApi: LocationsApi,
		locationColorApi: ColorsApi,
		projectMembersApi: ProjectMembersApi,
		priorityList: PriorityList,
		varianceList: VarianceList,
		ticketList: TicketList,
		calendarWorkdays: CalendarWorkdays
	) {
		this.projectMembersApi = projectMembersApi;
		this.rolesApi = rolesApi;
		this.locationsApi = locationsApi;
		this.roleColorApi = roleColorApi;
		this.locationColorApi = locationColorApi;
		this.priorityList = priorityList;
		this.varianceList = varianceList;
		this.ticketList = ticketList;
		this.calendarWorkdays = calendarWorkdays;

		this.loaded = true;
	}

	public boom() {
		// Class observers
		this.projectId.next(null);
		this.plan$.next(null);
		this.ticketList$.next(null);
		this.filteredTicketList$.next(null);
		this.authedProjectMember$.next(null);
		this.selectedTicket$.next(null);
		this.roles$.next([]);
		this.locations$.next([]);
		this.projectMembers$.next([]);
		this.priorityList$.next([]);
		this.varianceList$.next([]);

		// Assigned at setupCustomFieldApi
		this.customFieldsApi.destroy();
		this.customFieldsApi = null;

		// Assigned at setup
		if (this.virtualListManager) {
			this.virtualListManager.stopTicketIndexQuery();
			this.virtualListManager = null;
		}
		this.projectMembersApi.destroy();
		this.projectMembersApi = null;
		this.rolesApi.destroy();
		this.rolesApi = null;
		this.locationsApi.destroy();
		this.locationsApi = null;
		this.roleColorApi.destroy();
		this.roleColorApi = null;
		this.locationColorApi.destroy();
		this.locationColorApi = null;
		this.priorityList.destroy();
		this.priorityList = null;
		this.varianceList.destroy();
		this.varianceList = null;
		this.ticketList.destroy();
		this.ticketList = null;
		this.calendarWorkdays.destroy();
		this.calendarWorkdays = null;

		this.destroyables.forEach((d) => d.destroy());
		this.destroyables = [];

		this.shutdownSubject$.next();

		this.loaded = false;
	}
}
